import {
    Activity,
    AlertTriangle,
    ArrowRight,
    Gauge,
    TrendingDown,
    TrendingUp,
} from 'lucide-react';
import React from 'react';

import type { TreasurySnapshot } from '../types';
import { cn, formatBps, formatYield } from '../utils/formatters';

// --- 1. Reusable Internal Card Component ---
interface KpiCardProps {
    title: string;
    value: React.ReactNode;
    subtitle: React.ReactNode;
    icon: React.ElementType;
    valueColor?: string;
    badgeText?: string;
    badgeColor?: string;
}

const KpiCard: React.FC<KpiCardProps> = ({
    title,
    value,
    subtitle,
    icon: Icon,
    valueColor = 'text-slate-900 dark:text-white',
    badgeText,
    badgeColor = 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300 border-slate-200 dark:border-slate-700',
}) => (
    <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl p-5 shadow-sm transition-all hover:shadow-md flex flex-col justify-between h-full relative overflow-hidden group">
        <div>
            <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-slate-500 dark:text-slate-400 tracking-tight uppercase">
                    {title}
                </h3>
                <div className="p-1.5 bg-slate-50 dark:bg-slate-800/50 rounded-lg border border-slate-100 dark:border-slate-700 transition-colors group-hover:border-slate-200 dark:group-hover:border-slate-600">
                    <Icon className="w-4 h-4 text-slate-400 dark:text-slate-500" />
                </div>
            </div>
            <div className="flex items-baseline gap-2 mb-1">
                <span className={cn('text-3xl font-bold tracking-tight', valueColor)}>
                    {value}
                </span>
                {badgeText && (
                    <span
                        className={cn(
                            'text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border',
                            badgeColor,
                        )}
                    >
                        {badgeText}
                    </span>
                )}
            </div>
        </div>
        <div className="mt-4 flex items-center text-xs font-medium text-slate-500 dark:text-slate-400">
            {subtitle}
        </div>
    </div>
);

// --- 2. Main KPI Cards Orchestrator ---
interface Props {
    snapshot: TreasurySnapshot;
}

export const TreasuryKpiCards: React.FC<Props> = ({ snapshot }) => {
    // Logic 1: 10Y-2Y Spread — inverted (< 0) is the classic recession signal.
    const spread = snapshot.spread_10y_2y_bps ?? 0;
    const isInverted = spread < 0;
    const spreadColor = isInverted
        ? 'text-rose-600 dark:text-rose-400'
        : 'text-emerald-600 dark:text-emerald-400';
    const spreadBadgeColor = isInverted
        ? 'bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-900/30 dark:text-rose-400 dark:border-rose-800'
        : 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-400 dark:border-emerald-800';

    // Logic 2: Regime — Bull green, Bear red, otherwise neutral slate.
    const regime = snapshot.market_regime || 'Neutral';
    const isBull = regime.includes('Bull');
    const isBear = regime.includes('Bear');
    const regimeColor = isBull
        ? 'text-emerald-600 dark:text-emerald-400'
        : isBear
          ? 'text-rose-600 dark:text-rose-400'
          : 'text-slate-700 dark:text-slate-300';

    // Logic 3: Momentum — inverse bond math. Rising yields (mom > 0) are
    // bearish for bond prices (rose); falling yields are bullish (emerald).
    const mom = snapshot.momentum_10y_bps ?? 0;
    const isYieldRising = mom > 0;
    const isYieldFalling = mom < 0;
    const momColor = isYieldRising
        ? 'text-rose-600 dark:text-rose-400'
        : isYieldFalling
          ? 'text-emerald-600 dark:text-emerald-400'
          : 'text-slate-500 dark:text-slate-400';

    return (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-5 mb-8">
            <KpiCard
                title="10Y - 2Y Spread"
                value={formatBps(snapshot.spread_10y_2y_bps)}
                valueColor={spreadColor}
                icon={isInverted ? AlertTriangle : Activity}
                badgeText={snapshot.curve_shape || 'Unknown'}
                badgeColor={spreadBadgeColor}
                subtitle={
                    <span className="flex items-center gap-1.5">
                        Recession indicator when inverted
                    </span>
                }
            />

            <KpiCard
                title="Market Regime"
                value={regime}
                valueColor={regimeColor}
                icon={isBull ? TrendingUp : isBear ? TrendingDown : ArrowRight}
                subtitle="Institutional quant classification"
            />

            <KpiCard
                title="10Y Benchmark Yield"
                value={formatYield(snapshot.year10)}
                icon={Gauge}
                subtitle={
                    <div className="flex items-center gap-1.5">
                        <span>Daily Momentum:</span>
                        <span className={cn('font-bold flex items-center gap-0.5', momColor)}>
                            {isYieldRising ? (
                                <TrendingUp className="w-3 h-3" />
                            ) : isYieldFalling ? (
                                <TrendingDown className="w-3 h-3" />
                            ) : null}
                            {formatBps(mom)}
                        </span>
                    </div>
                }
            />

            <KpiCard
                title="Macro Volatility"
                value={
                    snapshot.volatility_10y_63d != null
                        ? (snapshot.volatility_10y_63d * 100).toFixed(1)
                        : '--'
                }
                valueColor="text-indigo-600 dark:text-indigo-400"
                icon={Activity}
                badgeText="BPS / 63D"
                badgeColor="bg-indigo-50 text-indigo-700 border-indigo-200 dark:bg-indigo-900/30 dark:text-indigo-400 dark:border-indigo-800"
                subtitle="1 Quarter rolling standard deviation"
            />
        </div>
    );
};

export default TreasuryKpiCards;
